import { ProfileAPIService } from './AxiosAPIService';
import {
  Group,
  GroupsResponse,
  PostGroupRequest,
  PatchGroupRequest,
  DeleteGroupRequest,
  MergeGroupsRequest,
  CloneGroupsRequest,
  AddUserToGroupRequest,
  AddUsersToGroupRequest,
  AddVehicleToGroupRequest,
  AddVehiclesToGroupRequest,
  AddAllVehiclesToGroupRequest,
} from '../types/Groups';
import { logger } from './SharedService';
import { UsersResponse } from '../types/Users';
import { VehiclesResponse } from '../types/Vehicles';

const GROUP_ENDPOINT = '/fleets';

export const getGroups = async (fleetId: string, query: string): Promise<GroupsResponse> => {
  logger.info('GET', 'REQUEST', 'getGroups', `${GROUP_ENDPOINT}/${fleetId}/groups${query}`);
  const { data } = await ProfileAPIService.api
    .get<GroupsResponse>(`${GROUP_ENDPOINT}/${fleetId}/groups${query}`);
  logger.info('GET', 'RESPONSE', 'getGroups', data);
  return data;
};

export const getGroupById = async (id: string, fleetId: string): Promise<Group> => {
  logger.info('GET', 'REQUEST', 'getGroupById', `${GROUP_ENDPOINT}/${fleetId}/groups/${id}`);
  const { data } = await ProfileAPIService.api
    .get<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/${id}`);
  logger.info('GET', 'RESPONSE', 'getGroupById', data);
  return data;
};

export const postGroup = async (payload: PostGroupRequest): Promise<Group> => {
  const {
    fleetId, name,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups`, {
    name,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const patchGroup = async (payload: PatchGroupRequest): Promise<Group> => {
  const {
    id, fleetId, name,
  } = payload;

  const { data } = await ProfileAPIService.api
    .patch<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/${id}`, {
    name,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const deleteGroup = async (payload: DeleteGroupRequest): Promise<Group> => {
  const { fleetId, id } = payload;
  const { data } = await ProfileAPIService.api
    .delete<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/${id}`);
  return data;
};

export const getUsersByGroupId = async (groupId: string, fleetId: string, query = ''): Promise<UsersResponse> => {
  const { data } = await ProfileAPIService.api
    .get<UsersResponse>(`${GROUP_ENDPOINT}/${fleetId}/groups/${groupId}/users${query}`);
  return data;
};

export const getVehiclesByGroupId = async (groupId: string, fleetId: string, query = ''): Promise<VehiclesResponse> => {
  const { data } = await ProfileAPIService.api
    .get<VehiclesResponse>(`${GROUP_ENDPOINT}/${fleetId}/groups/${groupId}/vehicles${query}`);
  return data;
};

export const addUserToGroup = async (payload: AddUserToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId, userId,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/user`, {
    groupId, userId,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const addUsersToGroup = async (payload: AddUsersToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId, userIds,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/user/bulk`, {
    groupId, userIds,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const removeUserFromGroup = async (payload: AddUserToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId, userId,
  } = payload;

  const { data } = await ProfileAPIService.api
    .delete<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/user/`, {
    data: {
      groupId, userId,
    },
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const addVehicleToGroup = async (payload: AddVehicleToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId, vehicleId,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/vehicle`, {
    groupId, vehicleId,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const addVehiclesToGroup = async (payload: AddVehiclesToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId, vehicleIds,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/vehicle/bulk`, {
    groupId, vehicleIds,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const addAllVehiclesToGroup = async (payload: AddAllVehiclesToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/vehicle/all`, {
    groupId,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const removeVehicleFromGroup = async (payload: AddVehicleToGroupRequest): Promise<Group> => {
  const {
    fleetId, groupId, vehicleId,
  } = payload;

  const { data } = await ProfileAPIService.api
    .delete<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/vehicle`, {
    data: {
      groupId, vehicleId,
    },
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const mergeGroups = async (payload: MergeGroupsRequest): Promise<Group> => {
  const {
    fleetId, name, groupIds, mergeUsers, deleteOriginGroup,
  } = payload;
  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/merge`, {
    name, groupIds, mergeUsers, deleteOriginGroup,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const cloneGroups = async (payload: CloneGroupsRequest): Promise<Group> => {
  const {
    fleetId, groupIds, mergeUsers,
  } = payload;
  const { data } = await ProfileAPIService.api
    .post<Group>(`${GROUP_ENDPOINT}/${fleetId}/groups/clone`, {
    groupIds, mergeUsers,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};
